import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import {
    useNodeMapActions,
    useNodeMapShallowSelector,
} from 'components/WorkspaceEditor/features/keyOperations'
import {
    useNotification,
    useTransactionManager,
} from 'components/WorkspaceEditor/services'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
    DESIGNATION_MISSING_INFO_ERROR_MESSAGE,
    DESIGNATION_PLACEHOLDER,
    DESIGNATION_SAVE_ERROR_MESSAGE,
    DESIGNATION_UPDATE_SUCCESS_MESSAGE,
} from '../constants'
import { createOptimisticDesignationUpdate } from '../services/optimisticUpdater'
import {
    PRODUCT_TYPE_ID_ERROR_MESSAGES,
    resolveProductTypeIds,
} from '../utils/resolveProductTypeIds'
import { useDrawingDesignationMutation } from './useDrawingDesignationMutation'

interface UseDrawingDesignationHandlersProps {
    currentDesignation: string
    contentItemId: string
    /** Caches designation using stable baseElementId before save (Bug 2583848) */
    onDesignationSave?: (baseElementId: string, designation: string) => void
}

interface DrawingDesignationHandlers {
    handleValueChange: (
        updatedItem: Partial<TextContentElement>,
        isSelected: boolean
    ) => Promise<void>
    isSaving: boolean
    /** Empty when content not added to PT project - use for disabled/inactive state */
    productTypeElementId: string
    /** Empty when content not added to project family - use for disabled/inactive state */
    productFamilyId: string
    /** Library element ID - stable across add/remove cycles, used for caching */
    baseElementId: string
}

/**
 * Hook that provides handlers for drawing designation changes with
 * race condition prevention, optimistic updates, and error handling.
 */
export const useDrawingDesignationHandlers = ({
    currentDesignation,
    contentItemId,
    onDesignationSave,
}: UseDrawingDesignationHandlersProps): DrawingDesignationHandlers => {
    const { error } = useNotification()
    const { executeWithOptimistic } = useTransactionManager()

    const {
        selectedProjectElementNode,
        projectId,
        isTreeMappingInProgress,
        contentItemViewNodes,
        setContentItemViewNodes,
        familyAddedProductTypes,
        markElementAsBeingProcessed,
        markElementAsRecentlyProcessed,
        isElementBeingProcessed,
        isElementRecentlyProcessed,
        elementsBeingProcessed,
    } = useShallowSelector((state) => ({
        selectedProjectElementNode: state.selectedProjectElementNode,
        projectId: state.projectId,
        isTreeMappingInProgress: state.isTreeMappingInProgress,
        contentItemViewNodes: state.contentItemViewNodes,
        setContentItemViewNodes: state.actions.setContentItemViewNodes,
        familyAddedProductTypes: state.familyAddedProductTypes || [],
        markElementAsBeingProcessed: state.actions.markElementAsBeingProcessed,
        markElementAsRecentlyProcessed:
            state.actions.markElementAsRecentlyProcessed,
        isElementBeingProcessed: state.actions.isElementBeingProcessed,
        isElementRecentlyProcessed: state.actions.isElementRecentlyProcessed,
        elementsBeingProcessed: state.elementsBeingProcessed, // For re-render on state change
    }))

    const { parentFamilyId } = useElementItemShallowSelector((s) => ({
        parentFamilyId: s.elementItemValue.parentFamilyId,
    }))

    const { isSaveOperationInProgress } = useNodeMapShallowSelector(
        (state) => ({
            isSaveOperationInProgress: state.isSaveOperationInProgress,
        })
    )
    const { setSaveOperationInProgress } = useNodeMapActions()

    const { saveDesignation, isSaving } = useDrawingDesignationMutation()
    const lastSavedDesignationRef = useRef(currentDesignation)

    // Sync ref with server state for change detection
    useEffect(() => {
        lastSavedDesignationRef.current = currentDesignation
    }, [currentDesignation])

    // Resolve and validate product type IDs (or article IDs for articles)
    const resolvedIds = useMemo(() => {
        if (!projectId) {
            return {
                productTypeElementId: '',
                productFamilyId: '',
                baseElementId: '',
                error: undefined,
            }
        }

        // Resolve element IDs for both PTs and Articles
        // - Direct selection (PT/Article selected): uses selectedProjectElementNode.elementId
        // - Family selection (viewing PT/Article in Family): resolves via familyAddedProductTypes
        const {
            productTypeElementId,
            productFamilyId,
            baseElementId,
            error: resolveError,
        } = resolveProductTypeIds({
            selectedProjectElementNode,
            contentItemViewNodes,
            contentItemId,
            familyAddedProductTypes,
            parentFamilyId,
        })

        return {
            productTypeElementId,
            productFamilyId,
            baseElementId,
            error: resolveError,
        }
    }, [
        projectId,
        selectedProjectElementNode,
        contentItemViewNodes,
        contentItemId,
        familyAddedProductTypes,
        parentFamilyId,
    ])

    const handleValueChange = useCallback(
        async (
            updatedItem: Partial<TextContentElement>,
            _isSelected: boolean
        ) => {
            // Skip if element is currently or recently being processed
            const isElementBusy =
                isElementBeingProcessed?.(contentItemId) ||
                isElementRecentlyProcessed?.(contentItemId)
            if (isElementBusy) {
                return
            }

            const rawText = updatedItem.text?.trim() || ''

            const newDesignation =
                rawText === DESIGNATION_PLACEHOLDER ? '' : rawText

            // Skip if system busy or no change
            const isSystemBusy =
                isSaveOperationInProgress || (isTreeMappingInProgress ?? false)
            if (isSystemBusy) return
            if (newDesignation === lastSavedDesignationRef.current) return

            const {
                productTypeElementId,
                productFamilyId,
                error: resolveError,
            } = resolvedIds

            if (resolveError) {
                error({ message: PRODUCT_TYPE_ID_ERROR_MESSAGES[resolveError] })
                setSaveOperationInProgress(false)
                return
            }

            if (!projectId) {
                error({
                    message: DESIGNATION_MISSING_INFO_ERROR_MESSAGE,
                })
                setSaveOperationInProgress(false)
                return
            }

            const optimisticTreeViewNodes = createOptimisticDesignationUpdate(
                contentItemViewNodes,
                contentItemId,
                newDesignation,
                lastSavedDesignationRef.current
            )

            // Cache designation using baseElementId (stable across add/remove cycles)
            const { baseElementId } = resolvedIds
            if (baseElementId) {
                onDesignationSave?.(baseElementId, newDesignation)
            }

            // Mark as processing before async operation to prevent race conditions
            markElementAsBeingProcessed?.(contentItemId)
            setSaveOperationInProgress(true)

            executeWithOptimistic({
                fallbackState: '',
                asyncAction: async () => {
                    await saveDesignation({
                        productTypeElementId,
                        projectId,
                        designation: newDesignation,
                        productFamilyId,
                    })
                },
                successMessage: DESIGNATION_UPDATE_SUCCESS_MESSAGE,
                successCbFn: async () => {
                    lastSavedDesignationRef.current = newDesignation
                    markElementAsRecentlyProcessed?.(contentItemId)
                    setSaveOperationInProgress(false)
                },
                errorCbFn: () => {
                    markElementAsRecentlyProcessed?.(contentItemId)
                    setSaveOperationInProgress(false)
                    error({ message: DESIGNATION_SAVE_ERROR_MESSAGE })
                },
            })

            setContentItemViewNodes(optimisticTreeViewNodes)
        },
        [
            contentItemId,
            isElementBeingProcessed,
            isElementRecentlyProcessed,
            isSaveOperationInProgress,
            isTreeMappingInProgress,
            projectId,
            resolvedIds,
            contentItemViewNodes,
            saveDesignation,
            executeWithOptimistic,
            markElementAsBeingProcessed,
            markElementAsRecentlyProcessed,
            setSaveOperationInProgress,
            setContentItemViewNodes,
            error,
            onDesignationSave,
        ]
    )

    const isCurrentElementBeingProcessed =
        elementsBeingProcessed?.has(contentItemId) ?? false

    return {
        handleValueChange,
        isSaving: isSaving || isCurrentElementBeingProcessed,
        productTypeElementId: resolvedIds.productTypeElementId,
        productFamilyId: resolvedIds.productFamilyId,
        baseElementId: resolvedIds.baseElementId,
    }
}
